import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string;
  requiredMark?: boolean;
  options: SelectOption[];
  placeholder?: string;
  containerClassName?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    requiredMark,
    options,
    placeholder,
    className,
    containerClassName,
    id,
    value,
    ...rest
  },
  ref,
) {
  return (
    <div className={cn('w-full', containerClassName)}>
      {label ? (
        <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-foreground-800">
          {label}
          {requiredMark ? <span className="text-rose-600"> *</span> : null}
        </label>
      ) : null}
      <div className="relative">
        <select
          id={id}
          ref={ref}
          value={value}
          aria-invalid={Boolean(error)}
          className={cn(
            'h-11 w-full appearance-none rounded-md border bg-background-50 ps-3.5 pe-10 text-sm text-foreground-950 transition-colors duration-200 ease-smooth',
            value ? 'text-foreground-950' : 'text-foreground-400',
            error
              ? 'border-rose-300 focus:border-rose-400'
              : 'border-background-300 hover:border-background-400 focus:border-accent-400',
            'focus:outline-none focus:ring-4 focus:ring-accent-200/60 cursor-pointer',
            className,
          )}
          {...rest}
        >
          {placeholder ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute inset-y-0 end-3 flex items-center w-5 h-5 justify-center text-foreground-400">
          <i className="ri-arrow-down-s-line text-lg leading-none" aria-hidden="true" />
        </span>
      </div>
      {error ? (
        <p className="mt-1 text-xs text-rose-600">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-foreground-500">{hint}</p>
      ) : null}
    </div>
  );
});

export default Select;